import { catchError, map, mergeMap, of, startWith } from "rxjs";
import { ItemStatus } from "@focus/interfaces/lib/common/itemFetchStatus";
import { getErrorMessage } from "@focus/services/lib/helpers/errorParser";
import type { Duck, FocusAjaxError } from "@focus/interfaces";
import { ofType } from "redux-observable";
import type { RootState } from "../../store";

export default function createServerConfiguration(): Duck {
  const url = `/api/v1/serverconfig`;

  // define constants
  const SET = `SERVER_CONFIG/SET`;
  const FETCH = `SERVER_CONFIG/FETCH_START`;
  const FETCH_ERROR = `SERVER_CONFIG/FETCH_ERROR`;
  const FETCH_PENDING = `SERVER_CONFIG/FETCH_PENDING`;

  // ACTIONS
  function fetchItems() {
    return { type: FETCH };
  }
  function setItems(payload) {
    return { type: SET, payload };
  }
  function setItemsPending() {
    return { type: FETCH_PENDING };
  }
  function setError(payload: string) {
    return { type: FETCH_ERROR, payload };
  }

  // REDUCER
  // default state
  const initialState = {
    config: {
      status: "",
      errorMessage: "",
      content: {
        applications: [],
        configurations: [
          { name: "fleet", available: true },
          { name: "conjunctions", available: true },
          { name: "orbits", available: true },
          { name: "upload", available: true },
          { name: "phonebook", available: true },
          { name: "config", available: true },
        ],
      },
    },
  };

  /**
   * Reducer to handle the configuration
   *
   * @param  {Object} state={}
   * @param  {Object} action
   */
  function reducer(state = initialState, action) {
    switch (action.type) {
      case FETCH_PENDING:
        return Object.assign({}, state, {
          items: [],
          status: ItemStatus.PENDING,
          errorMessage: "",
        });
      case FETCH_ERROR:
        return Object.assign({}, state, {
          items: [],
          status: ItemStatus.ERROR,
          errorMessage: action.payload,
        });
      case SET:
        return Object.assign({}, state, {
          items: action.payload,
          status: ItemStatus.READY,
          errorMessage: "",
          save: undefined,
        });
      default:
        return state;
    }
  }

  // SELECTORS
  const selectors = {
    getStatus: (state: RootState) => state.serverConfig.status,
    getError: (state: RootState) => state.serverConfig.errorMessage,
    getItems: (state: RootState) => state.serverConfig.items,
  };

  /**
   * Epic creator for the fetch
   *
   * @param  {} action$ Actions stream
   * @param  {} _
   * @param  {} {ajax} The ajax service
   */
  const fetchItemsEpic = (action$, _, { ajax }) =>
    action$.pipe(
      ofType(FETCH),
      mergeMap((action) =>
        ajax.getJSON(url).pipe(
          map((response) => setItems(response)),
          catchError((error: FocusAjaxError, _source) => {
            return of(setError(getErrorMessage(error)));
          }),
          startWith(setItemsPending())
        )
      )
    );

  const actionTypes = {
    SET,
    FETCH,
    FETCH_ERROR,
    FETCH_PENDING,
  };

  const actions = {
    fetchItems,
    setItems,
    setError,
  };

  const middleware = [fetchItemsEpic];
  return { actions, reducer, middleware, selectors, actionTypes };
}
